import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, useAuth } from '../App';
import { statusFromAlerts, fmt, relTime } from '../utils/locationHelpers';
import { money } from '../utils/format';
import { canViewCosts } from '../utils/permissions';
import {
  ArrowLeft, MapPin, Package, AlertTriangle, DollarSign, FileText,
  Eye, ShoppingCart, ArrowLeftRight, Clock, Pencil
} from 'lucide-react';

export default function LocationDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [loc, setLoc] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api('/api/locations/' + id),
      api('/api/locations/' + id + '/summary'),
    ]).then(([lr, sr]) => {
      setLoc(lr.data);
      setSummary(sr.data);
    }).catch(() => nav('/locations'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading || !loc || !summary) return (
    <div style={{ padding: 40, color: 'var(--muted)' }}>Loading location...</div>
  );

  const status = statusFromAlerts(summary.alert_products || 0);
  const alerts = summary.alert_products || 0;
  const topAlerts = summary.top_alerts || [];

  return (
    <div>
      {/* Back + Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button onClick={() => nav('/locations')} style={{
          background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8,
          padding: '8px 10px', cursor: 'pointer', color: 'var(--muted)', transition: 'all .12s',
        }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--gold)'; e.currentTarget.style.color = 'var(--gold)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--muted)'; }}
        >
          <ArrowLeft size={16} />
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 style={{ fontFamily: 'Lora,serif', fontSize: 24, margin: 0 }}>{loc.name}</h1>
            <span style={{
              padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 700,
              letterSpacing: '.6px', background: status.bg, color: status.color,
            }}>{status.label.toUpperCase()}</span>
          </div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>
            <MapPin size={12} style={{ verticalAlign: '-1px', marginRight: 5, opacity: .5 }} />
            {loc.address ? `${loc.address}, ${loc.city}, ${loc.state} ${loc.zip}` : 'No address'}
            {loc.phone && <span style={{ marginLeft: 16, color: 'var(--faint)' }}>{loc.phone}</span>}
          </div>
        </div>
      </div>

      {/* KPI Strip */}
      <div style={{ display: 'flex', gap: 1, marginBottom: 28, borderRadius: 14, overflow: 'hidden', border: '1px solid var(--border)' }}>
        {[
          { label: 'Products', val: summary.total_products, icon: Package, color: '#C9A84C' },
          { label: 'Alerts', val: alerts, icon: AlertTriangle, color: '#E74C3C', urgent: alerts > 0 },
          { label: 'Out of Stock', val: summary.out_of_stock || 0, icon: AlertTriangle, color: '#E74C3C', urgent: (summary.out_of_stock || 0) > 0 },
          { label: 'Value', val: canViewCosts(user) ? money(summary.inventory_value || 0) : '•••', icon: DollarSign, color: '#2ECC71' },
          { label: 'POs (30d)', val: summary.recent_pos, icon: FileText, color: '#78A8E8' },
        ].map((k, i) => (
          <div key={i} style={{
            flex: 1, padding: '18px 16px', textAlign: 'center',
            background: k.urgent ? k.color + '08' : 'var(--card)',
            borderRight: i < 4 ? '1px solid var(--border)' : 'none',
          }}>
            <k.icon size={16} style={{ color: k.color, opacity: .5, marginBottom: 6 }} />
            <div style={{ fontFamily: 'Lora,serif', fontSize: 22, fontWeight: 700, color: k.urgent ? k.color : 'var(--gold)', lineHeight: 1 }}>{k.val}</div>
            <div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginTop: 5 }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 28, flexWrap: 'wrap' }}>
        {[
          { label: 'View Inventory', icon: Eye, color: '#4A9EBF', action: () => nav('/inventory?location_id=' + id) },
          { label: 'View Alerts', icon: AlertTriangle, color: '#E74C3C', action: () => nav('/inventory?location_id=' + id + '&status=reorder') },
          { label: 'Create PO', icon: ShoppingCart, color: '#C9A84C', action: () => nav('/orders') },
          { label: 'Transfer Stock', icon: ArrowLeftRight, color: '#5BC4C0', action: () => nav('/transfers') },
        ].map((qa, i) => (
          <button key={i} onClick={qa.action} style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px',
            background: i === 0 ? qa.color + '18' : 'var(--card)',
            border: '1px solid ' + (i === 0 ? qa.color + '40' : 'var(--border)'),
            borderRadius: 10, color: i === 0 ? qa.color : 'var(--white)',
            fontFamily: 'DM Sans,sans-serif', fontSize: 13, fontWeight: 600,
            cursor: 'pointer', transition: 'all .15s',
          }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = qa.color; e.currentTarget.style.background = qa.color + '14'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = i === 0 ? qa.color + '40' : 'var(--border)'; e.currentTarget.style.background = i === 0 ? qa.color + '18' : 'var(--card)'; e.currentTarget.style.transform = 'none'; }}
          >
            <qa.icon size={15} style={{ color: qa.color }} />{qa.label}
          </button>
        ))}
      </div>

      {/* Two-column: Alerts + Freshness */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 28 }}>
        {/* Alert Preview */}
        <div className="card" style={{ padding: 20 }}>
          <h3 style={{ fontFamily: 'Lora,serif', fontSize: 15, color: alerts > 0 ? status.color : 'var(--gold)', marginBottom: 16 }}>
            {alerts > 0 ? 'Items Needing Attention' : 'Stock Status'}
          </h3>
          {alerts === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#2ECC71', fontSize: 14 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#2ECC71' }} />
              All stock levels healthy
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {topAlerts.map((a, i) => (
                <div key={i} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 14px', background: 'var(--navy)', borderRadius: 10,
                  borderLeft: '3px solid ' + (Number(a.current_stock) === 0 ? '#E74C3C' : '#F39C12'),
                }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{a.product_name}</div>
                    <div style={{ fontSize: 11, color: 'var(--faint)' }}>{a.sku || ''}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: Number(a.current_stock) === 0 ? '#E74C3C' : '#F39C12' }}>{fmt(a.current_stock)}</div>
                    <div style={{ fontSize: 10, color: 'var(--faint)' }}>min {fmt(a.reorder_point)}</div>
                  </div>
                </div>
              ))}
              {alerts > topAlerts.length && (
                <button onClick={() => nav('/inventory?location_id=' + id + '&status=reorder')}
                  style={{ background: 'none', border: 'none', color: 'var(--gold)', fontSize: 13, cursor: 'pointer', textAlign: 'left', padding: '4px 0' }}>
                  View all {alerts} alerts &rarr;
                </button>
              )}
              {/* TODO: When backend supports full alert list endpoint, load all alerts here */}
            </div>
          )}
        </div>

        {/* Activity / Freshness */}
        <div className="card" style={{ padding: 20 }}>
          <h3 style={{ fontFamily: 'Lora,serif', fontSize: 15, color: 'var(--gold)', marginBottom: 16 }}>Activity</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: '#78A8E814', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Clock size={16} style={{ color: '#78A8E8' }} />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>Last Inventory Count</div>
                <div style={{ fontSize: 12, color: 'var(--faint)' }}>{relTime(summary.last_count)}</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: '#C9A84C14', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FileText size={16} style={{ color: '#C9A84C' }} />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>Last Purchase Order</div>
                <div style={{ fontSize: 12, color: 'var(--faint)' }}>{relTime(summary.last_po)}</div>
              </div>
            </div>
            {/* TODO: Add last transfer date when backend supports it */}
            {/* TODO: Add last waste log date when backend supports it */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, opacity: .5 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: '#5BC4C014', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ArrowLeftRight size={16} style={{ color: '#5BC4C0' }} />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>Last Transfer</div>
                <div style={{ fontSize: 12, color: 'var(--faint)' }}>Coming soon</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* TODO: Add inventory table for this location */}
      {/* TODO: Add PO history for this location */}
      {/* TODO: Add waste log for this location */}

      <style>{`
        @media (max-width:768px) {
          [style*="gridTemplateColumns: 1fr 1fr"] { grid-template-columns:1fr !important; }
        }
      `}</style>
    </div>
  );
}
